import { NODE_SUPERTYPE } from '../../common/linqset-model';
import { AnalysisNode } from '../../common/analysis-model';
import {
  BusinessModel,
  BusinessProcessNodeDefinition,
  BusinessProcessNodeType,
} from './business-process.model';

// how LINQ nodes are drawn in the business process graph. LINQ has no decision nodes, so 'decision' is unused
export const toProcessNodeType = (
  node: Pick<AnalysisNode, 'superType' | 'isStarting'>,
): BusinessProcessNodeType => {
  // a model not shown, which the models shown join or use
  if ((node as { otherModel?: boolean }).otherModel) return 'model';
  switch (node.superType) {
    case NODE_SUPERTYPE.ACTION:
      return node.isStarting ? 'trigger' : 'action';
    case NODE_SUPERTYPE.INFORMATION:
      return 'output';
    case NODE_SUPERTYPE.OUTCOMES:
      return 'outcome';
    default:
      return 'action';
  }
};

// how much of the model colour is kept in a node's fill; the rest is white
const FILL_STRENGTH = 0.15;
const FALLBACK_FILL = '#f8fafc';

// outlines need at least this contrast against the white background to stand out (WCAG non-text contrast)
const MIN_OUTLINE_CONTRAST = 3;

export const createBusinessModel = (
  id: string,
  name: string,
  colour: string,
): BusinessModel => {
  const hex = toHexColour(colour);
  return {
    id,
    name,
    // light colours such as Gold are darkened for the outline and accent; the fill keeps the original colour
    color: hex ? darkenForContrast(hex, MIN_OUTLINE_CONTRAST) : colour,
    fill: hex ? tint(hex, FILL_STRENGTH) : FALLBACK_FILL,
  };
};

// darkens a #rrggbb colour just enough to reach the contrast ratio against white; darker colours are unchanged
export const darkenForContrast = (hex: string, ratio: number): string => {
  let shaded = hex.toLowerCase();
  for (let strength = 1; contrastWithWhite(shaded) < ratio && strength > 0; strength -= 0.05) {
    shaded = shade(hex, strength);
  }
  return shaded;
};

// mixes a #rrggbb colour with black, keeping `strength` of the colour
export const shade = (hex: string, strength: number): string =>
  toHex(toChannels(hex).map((c) => c * Math.max(0, strength)));

export const contrastWithWhite = (hex: string): number =>
  1.05 / (relativeLuminance(hex) + 0.05);

const relativeLuminance = (hex: string): number => {
  const [r, g, b] = toChannels(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const toChannels = (hex: string): number[] =>
  [1, 3, 5].map((i) => parseInt(hex.substring(i, i + 2), 16));

const toHex = (channels: number[]): string =>
  '#' + channels.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('');

export const toProcessNodeDefinition = (
  node: AnalysisNode,
  model: BusinessModel,
): BusinessProcessNodeDefinition => ({
  id: node._key,
  type: toProcessNodeType(node),
  label: node.name || '',
  model,
});

// mixes a #rrggbb colour with white, keeping `strength` of the colour
export const tint = (hex: string, strength: number): string =>
  toHex(toChannels(hex).map((c) => c * strength + 255 * (1 - strength)));

// model colours may be CSS names (e.g. 'Crimson'), so let the browser resolve them to #rrggbb
export const toHexColour = (colour: string): string | undefined => {
  if (/^#[0-9a-f]{6}$/i.test(colour)) return colour.toLowerCase();
  try {
    const context = document.createElement('canvas').getContext('2d');
    if (!context) return undefined;
    context.fillStyle = '#000000';
    context.fillStyle = colour;
    const resolved = context.fillStyle;
    // an unknown name leaves the fill style unchanged
    if (/^#[0-9a-f]{6}$/i.test(resolved) && (resolved !== '#000000' || /^(black|#000000)$/i.test(colour)))
      return resolved;
  } catch {
    // no canvas available (e.g. running outside a browser)
  }
  return undefined;
};
